import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";

export interface TransferMethodConfigRepository {
  useBlockedMethods(): Resource<string[]>;
  update(blockedMethodKeys: string[]): Promise<void>;
}

const BLOCKED_METHODS_KEY = "blocked-transfer-methods";

export function useTransferMethodConfigRepository(): TransferMethodConfigRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  function useBlockedMethods(): Resource<string[]> {
    return useQuery(
      [BLOCKED_METHODS_KEY],
      async (): Promise<string[]> => {
        const dto = await apiClient.getBlockedTransferMethods();
        return dto.blockedMethodKeys ?? [];
      },
      {
        // Admin surface — always show the current server state on entry.
        staleTime: 0,
      },
    );
  }

  async function update(blockedMethodKeys: string[]): Promise<void> {
    await apiClient.updateBlockedTransferMethods(blockedMethodKeys);
    await queryClient.invalidateQueries({ queryKey: [BLOCKED_METHODS_KEY] });
    // The customer-facing dropdowns read blockedMethodKeys from logo-options,
    // so the admin's own session must refetch it to see the change take effect.
    await queryClient.invalidateQueries({ queryKey: ["logo-options"] });
    // The method catalogs now carry a 30 min staleTime (see LogoRepository);
    // nudge transfer-options too so a method change doesn't linger in this
    // admin's session. Prefix match covers every vendorId partition.
    await queryClient.invalidateQueries({ queryKey: ["transfer-options"] });
  }

  return {
    useBlockedMethods,
    update,
  };
}
